import { styled, keyframes, Box } from "@mui/material";
import React from "react";

// Typing dots bounce animation
const dotBounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-6px);
  }
`;

export const TypingDotsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '8px 12px',
  background: '#ececf0ff',
  borderRadius: '8px 8px 8px 0px',
});

export const TypingDot = styled(Box)({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#888',
  animation: `${dotBounce} 1.4s infinite ease-in-out both`,
  '&:nth-of-type(1)': {
    animationDelay: '-0.32s',
  },
  '&:nth-of-type(2)': {
    animationDelay: '-0.16s',
  },
  '&:nth-of-type(3)': {
    animationDelay: '0s',
  },
});

export const sendButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  background: '#EB0A1E',
  border: 'none',
  borderRadius: '50%',
  width: 30,
  height: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
};

export const botIconStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 24,
  right: 24,
  zIndex: 2000,
  background: "#d32f2f",
  color: "#fff",
  border: "none",
  borderRadius: "28px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  width: 160,
  height: 46,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
  fontSize: 15,
  fontWeight: 100,
  letterSpacing: 1,
  gap: 12,
};



export const ChatWindowContainer = styled('div')<{ $width?: number; $height?: number; $docked?: boolean }>(({ $width, $height, $docked }) => ($docked ? {
  position: "relative",
  width: "100%",
  height: "100%",
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #E8EBF0",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
} : {
  position: "fixed",
  bottom: 90,
  right: 24,
  width: $width || 700,
  height: $height || 620,
  minWidth: 380,
  minHeight: 400,
  maxWidth: '95vw',
  maxHeight: 'calc(100vh - 120px)',
  background: "#fff",
  borderRadius: "0 0 0 28px",
  border: "1.5px solid #C2C2C2",
  boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
  zIndex: 2100,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

export const ChatHeader = styled('div')({
  padding: 12,
  background: "#d32f2f",
  color: "#d32f2f",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: 15,
});

export const ChatBody = styled('div')({
  flex: 1,
  padding: 16,
  overflowY: "auto",
  overflowX: "hidden",
  background: "#ffffff",
});

export const ChatMessageRow = styled('div')<{ from: "user" | "bot" }>(({ from }) => ({
  marginBottom: 10,
  display: "flex",
  justifyContent: from === "user" ? "flex-end" : "flex-start",
}));

export const ChatMessage = styled('div')<{ from: "user" | "bot" }>(({ from }) => ({
  background: from === "user" ? "#1976d2" : "#ececf0ff",
  color: from === "user" ? "#fff" : "#222",
  borderRadius: "8px 8px 8px 0px",
  padding: "8px 12px",
  maxWidth: "80%",
  fontSize: 15,
  overflowWrap: "break-word",
  wordBreak: "break-word",
  overflow: "hidden",
}));

export const ChatFooter = styled('div')({
  padding: 12,
  borderTop: "1.5px solid #C2C2C2",
  background: "#fafafa",
  display: "flex",
  gap: 8,
});

export const ChatInput = styled('input')({
  flex: 1,
  padding: "8px 16px",
  borderRadius: "28px",
  border: "1px solid #d32f2f",
  fontSize: 16,
  outline: "none",
});

export const ChatSendButton = styled('button')<{ disabled?: boolean }>(({ disabled }) => ({
  background: "#1976d2",
  border: "none",
  borderRadius: "50%",
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
}));

export const HeaderTitle = styled('span')({
  color: '#fff',
});

export const HeaderButtonsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const IconButton = styled('button')({
  background: "none",
  border: "none",
  color: "#fff",
  fontSize: 16,
  cursor: "pointer",
  padding: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  transition: "background-color 0.2s",
  '&:hover': {
    backgroundColor: "rgba(255,255,255,0.1)",
  },
});

export const CloseButton = styled('button')({
  background: "none",
  border: "none",
  color: "#fff",
  fontSize: 20,
  cursor: "pointer",
});

export const FooterInputContainer = styled('div')({
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 40,
});

export const InputWrapper = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
});

export const CharLimitWarning = styled('span')({
  fontSize: 11,
  color: '#d32f2f',
  marginTop: 4,
  marginLeft: 8,
});

export const CharCounter = styled('span')<{
  $isWarning?: boolean;
  $isError?: boolean;
}>(({ $isWarning, $isError }) => {
  let color = '#888';

  if ($isError) {
    color = '#d32f2f';
  } else if ($isWarning) {
    color = '#ed6c02';
  }

  return {
    fontSize: 11,
    color,
    marginRight: 8,
    fontFamily: 'monospace',
  };
});

export const ScrollToBottomButton = styled('button')({
  position: 'absolute',
  bottom: 12,
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#fff',
  border: '1px solid #C2C2C2',
  borderRadius: '50%',
  width: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  transition: 'background 0.2s, box-shadow 0.2s',
  zIndex: 10,
  '&:hover': {
    background: '#f0f0f0',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
});

export const ChatBodyWrapper = styled('div')({
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const CopyResponseButton = styled('button')({
  position: 'sticky',
  top: 6,
  right: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.85)',
  border: '1px solid #ddd',
  borderRadius: '50%',
  width: 26,
  height: 26,
  padding: 0,
  cursor: 'pointer',
  boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
  opacity: 0,
  transition: 'opacity 0.15s, box-shadow 0.15s, background 0.15s',
  zIndex: 2,
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    background: '#fff',
  },
});

export const BotMessageContainer = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  flexDirection: 'column',
  width: '80%',
  minWidth: 0,
  overflow: 'hidden',
  [`&:hover button[aria-label="Copy response"]`]: {
    opacity: 1,
  },
});

export const ResizeHandle = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 22,
  height: 22,
  cursor: 'nwse-resize',
  zIndex: 10,
});

export const HeaderTitleGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const HeaderBotBadge = styled('div')({
  width: 26,
  height: 26,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.22)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 15,
});

export const WelcomeState = styled('div')({
  padding: '4px 4px 8px',
  background: "#ffffff"
});

export const WelcomeGreeting = styled('div')({
  fontSize: 20,
  fontWeight: 700,
  color: '#1B2559',
  marginBottom: 6,
});

export const WelcomeSubtitle = styled('div')({
  fontSize: 14,
  color: '#64748B',
  paddingBottom: 16,
  marginBottom: 18,
  borderBottom: '1px solid #E8EBF0',
});

export const TryAskingLabel = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 14,
  fontWeight: 700,
  color: '#1B2559',
  marginBottom: 12,
});

export const SuggestionCard = styled('button')({
  width: '100%',
  textAlign: 'left',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  background: '#fff',
  border: '1px solid #E8EBF0',
  borderRadius: 12,
  padding: '12px 14px',
  marginBottom: 12,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': {
    borderColor: '#EB0A1E',
    boxShadow: '0 4px 12px rgba(235,10,30,0.10)',
  },
});

export const SuggestionIcon = styled('div')({
  width: 30,
  height: 30,
  flexShrink: 0,
  borderRadius: '50%',
  background: '#FDECEC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#EB0A1E',
  fontSize: 15,
  fontWeight: 700,
});

export const SuggestionText = styled('span')({
  flex: 1,
  fontSize: 13.5,
  fontWeight: 500,
  color: '#334155',
  lineHeight: 1.35,
});

export const SuggestionChevron = styled('span')({
  color: '#EB0A1E',
  fontSize: 16,
  fontWeight: 700,
  flexShrink: 0,
});

export const FooterHint = styled('div')({
  fontSize: 11,
  color: '#94A3B8',
  textAlign: 'center',
  marginTop: 8,
});